/**
 * Orders Service
 * Handles all order-related operations
 */

const BASE_URL = process.env.NODE_ENV === 'production' 
  ? (process.env.REACT_APP_API_URL || '/api')
  : (process.env.REACT_APP_API_URL || 'http://localhost:5000/api');

/**
 * Create a new order
 */
export const createOrder = async (orderData) => {
  try {
    const { customer_name, customer_email, customer_phone, shipping_address, items } = orderData;

    if (!customer_name || !customer_email || !customer_phone || !shipping_address || !items || items.length === 0) {
      throw new Error('Missing required fields');
    }

    const response = await fetch(`${BASE_URL}/orders`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(orderData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create order');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
};

/**
 * Validate a coupon code against a cart subtotal (does not mark it as used)
 */
export const validateCoupon = async (code, subtotal) => {
  try {
    const response = await fetch(`${BASE_URL}/orders/validate-coupon`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code, subtotal }),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to validate coupon');
    }

    return data;
  } catch (error) {
    console.error('Error validating coupon:', error);
    throw error;
  }
};

/**
 * Get order by ID
 */
export const getOrderById = async (id) => {
  try {
    const response = await fetch(`${BASE_URL}/orders/${id}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Order not found');
      }
      throw new Error(`Failed to fetch order: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching order:', error);
    throw error;
  }
};

/**
 * Get order with items
 */
export const getOrderWithItems = async (id) => {
  try {
    const response = await fetch(`${BASE_URL}/orders/${id}/items`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Order not found');
      }
      throw new Error(`Failed to fetch order: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching order with items:', error);
    throw error;
  }
};

/**
 * Request return for an order
 */
export const requestReturn = async (orderId, returnData) => {
  try {
    const response = await fetch(`${BASE_URL}/orders/${orderId}/returns`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(returnData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to request return');
    }

    return await response.json();
  } catch (error) {
    console.error('Error requesting return:', error);
    throw error;
  }
};

/**
 * Get return details
 */
export const getReturnById = async (id) => {
  try {
    const response = await fetch(`${BASE_URL}/orders/returns/${id}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Return not found');
      }
      throw new Error(`Failed to fetch return: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching return:', error);
    throw error;
  }
};

const orderServiceExports = {
  createOrder,
  validateCoupon,
  getOrderById,
  getOrderWithItems,
  requestReturn,
  getReturnById,
};

export default orderServiceExports;
